import { BXORA, IDictionary } from '../../shared/utils/types';

import { RouteObject } from 'react-router-dom';

/**
 * Props for the restricted component
 *
 * Note that unauthorizedProps and renderUnauthorized are mutually exclusive
 *
 * @author Asbjørn Rysgaard Eriksen <are@caretaker.dk>
 */
export type RestrictedProps = BXORA<
    {
        /**
         * Permissions needed to access the child component. Can either be a single permission or an array of permissions
         */
        permissions: UserPermissions | UserPermissions[];
        children: JSX.Element;
    },
    [
        {
            /**
             * Props to pass to the child element if not authorized
             */
            unauthorizedProps?: IDictionary;
        },
        {
            /**
             * Component to show if not authorized
             */
            renderUnauthorized?: JSX.Element | string;
        }
    ]
>;

/**
 * Props for the RestrictedRoute component
 *
 * @author Asbjørn Rysgaard Eriksen <are@caretaker.dk>
 */
export interface RestrictedRouteProps {
    /**
     * Permissions needed to access the child component. Can either be a single permission or an array of permissions
     */
    permissions: UserPermissions | UserPermissions[];

    /**
     * Element to show when authorized
     */
    Children: JSX.Element | JSX.Element[];
}

export type RestrictedRouteObject = RouteObject & {
    permissions?: UserPermissions | UserPermissions[];
};

export enum UserPermissions {
    Admin = 'Admin',
    Bygningsdelskort = 'Bygningsdelskort',
    Bygningsdelskort_Create = 'Bygningsdelskort.Create',
    Bygningsdelskort_Lev = 'Bygningsdelskort.Lev',
    Bygningsdelskort_Bygherre = 'Bygningsdelskort.Bygherre',
    Data_Extraction = 'Udtræk',
    Dashboard = "Dashboard",
    Dashboard_Create = "Dashboard.Create",
    Dashboard_Update = "Dashboard.Update",
    Drift = 'Drift',
    Drift_Adhocjournal = 'Drift.Adhocjournal',
    Drift_Create = 'Drift.Create',
    Drift_Read = 'Drift.Read',
    Drift_Update = 'Drift.Update',
    Drift_Delete = 'Drift.Delete',
    Drift_Fasttrack = 'Drift.Fasttrack',
    Drift_Journal = 'Drift.Journal',
    Drift_Kontakter = 'Drift.Kontakter',
    Drift_Link = 'Drift.Link',
    Drift_Lister = 'Drift.Lister',
    Drift_Rul = 'Drift.Rul',
    Drift_Tegninger = 'Drift.Tegninger',
    Drift_QR = 'Drift.QR',
    Forsyning = 'Forsyning',
    Forsyning_Admin = 'Forsyning.Admin',
    Map = 'Map',
    Map_Update = 'Map.Update',
    Map_Delete = 'Map.Delete',
    Map_Skema = 'Map.Skema',
    Map_Copyskema = 'Map.Copyskema',
    MeldFejl = 'MeldFejl',
    OK = 'OK',
    Overview = 'Overview',
    Plan = 'Plan',
    QR_Vaelg = 'QR.Vaelg',
    Rapport = 'Rapport',
    Rekvisition = 'Rekvisition',
    Rekvisition_Create = 'Rekvisition.Create',
    Rekvisition_Update_Status = 'Rekvisition.Update.Status',
    Rekvisition_Update_Fields = 'Rekvisition.Update.Fields',
    Service = 'Service',
    Settings = 'Settings',
    Syn = 'Syn',
    Team = 'Drift.Team',

    Denied = 'Denied',
}
